import { valueToCollabJSON } from '@databases/collab/convert/to-collab';
import { entryToDatabase, fieldTypeMap } from '@databases/collab/convert/to-database';
import { type YDatabase } from '@databases/collab/y-database';
import { FieldType } from '@databases/model/field';
import { type AllValueJSON } from '@databases/model/value';
import {
  type CreateEntryDatabaseValue,
  type EntrySdkContract
} from '@databases/sdk-types';
import { type DatabaseValue } from '@databases/types/database';
import { err, ok, type Result } from 'neverthrow';
import { v4 as uuidv4 } from 'uuid';

import {
  createEntryIfAbsentSchema,
  createEntrySchema
} from '../domain/entries/create-schemas.ts';
import {
  addEntrySchema,
  removeEntrySchema,
  reorderEntriesSchema,
  type AddEntryInput,
  type RemoveEntryInput,
  type ReorderEntriesInput
} from '../domain/entries/schemas.ts';
import { createEntryValueValidator } from '../domain/entries/validators.ts';
import { assertEntryExists } from '../domain/guards/assert-entry-exists.ts';
import { assertEntryNotExists } from '../domain/guards/assert-entry-not-exists.ts';
import { assertEntryValueTypesSupported } from '../domain/guards/assert-entry-value-types-supported.ts';
import { assertIndexInRange } from '../domain/guards/assert-index-in-range.ts';
import { parse, tryMutate } from '../domain/result.ts';
import { addEntry } from '../handlers/entry/add-entry.ts';
import { createEntry } from '../handlers/entry/create-entry.ts';
import { deleteEntry } from '../handlers/entry/delete-entry.ts';
import { reorderEntries } from '../handlers/entry/reorder-entries.ts';
import { type CreateEntryError, type DatabaseEntry, type SdkError } from '../types.ts';

const DERIVED_FIELD_TYPES = new Set<FieldType>([
  FieldType.Backlink,
  FieldType.EntryDetails,
  FieldType.JiraIssueDetails,
  FieldType.PageDetails,
  FieldType.PageLabels,
  FieldType.PageStatus
]);

type CreateEntryFieldChange = {
  fieldId: string;
  value: CreateEntryDatabaseValue;
};

type UnvalidatedCreateEntryFieldChange = {
  fieldId: string;
  value: unknown;
};

type CollabFieldChange = {
  fieldId: string;
  fieldType: FieldType;
  value: AllValueJSON;
};

/**
 * Public API for reading and mutating database entries (rows).
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (ENTRY_NOT_FOUND, ENTRY_ALREADY_EXISTS, INDEX_OUT_OF_RANGE)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Entries are **not** gated by `isLocked` — content edits stay allowed
 * when the database structure is locked.
 */
export class EntrySdk implements EntrySdkContract {
  constructor(private readonly ydb: YDatabase) {}

  get(entryId: string): DatabaseEntry | undefined {
    const entry = this.ydb.entriesById[entryId];

    if (!entry) {
      return undefined;
    }

    return entryToDatabase(entryId, entry.toJSON());
  }

  list(): readonly DatabaseEntry[] {
    return this.ydb.entries.flatMap((entryId) => {
      const entry = this.get(entryId);
      return entry ? [entry] : [];
    });
  }

  add(input: AddEntryInput): Result<void, SdkError> {
    return parse(addEntrySchema, input).andThen(({ entry, index }) =>
      assertEntryNotExists(this.ydb, entry.id)
        .andThen(() => assertEntryValueTypesSupported(entry.values))
        .andThen(() =>
          index != null
            ? assertIndexInRange(index, this.ydb.entries.length)
            : ok(undefined)
        )
        .andThen(() =>
          tryMutate(() => addEntry(this.ydb, { entry: entry as DatabaseEntry, index }))
        )
    );
  }

  create(input?: {
    fieldChanges?: readonly CreateEntryFieldChange[];
  }): Result<{ entryId: string }, CreateEntryError | SdkError> {
    return parse(createEntrySchema, input).andThen((request) =>
      this.validateFieldChanges(request?.fieldChanges ?? []).andThen((fieldChanges) => {
        const entryId = uuidv4();
        return tryMutate(() => {
          createEntry(this.ydb, { entryId, fieldChanges });
          return { entryId };
        });
      })
    );
  }

  createIfAbsent(input: {
    entryId: string;
    fieldChanges?: readonly CreateEntryFieldChange[];
  }): Result<void, CreateEntryError | SdkError> {
    return parse(createEntryIfAbsentSchema, input).andThen(
      ({ entryId, fieldChanges }) => {
        if (this.ydb.entriesById[entryId]) {
          return ok(undefined);
        }

        return this.validateFieldChanges(fieldChanges ?? []).andThen((changes) =>
          tryMutate(() => createEntry(this.ydb, { entryId, fieldChanges: changes }))
        );
      }
    );
  }

  private validateFieldChanges(
    fieldChanges: readonly UnvalidatedCreateEntryFieldChange[]
  ): Result<CollabFieldChange[], CreateEntryError> {
    const errors: CreateEntryError['fieldErrors'] = [];
    const changes: CollabFieldChange[] = [];
    const seenFieldIds = new Set<string>();

    for (const change of fieldChanges) {
      if (seenFieldIds.has(change.fieldId)) {
        errors.push({ fieldId: change.fieldId, reason: 'DUPLICATE_FIELD_ID' });
        continue;
      }
      seenFieldIds.add(change.fieldId);

      const field = this.ydb.fieldsById[change.fieldId];
      if (!field || !this.ydb.fields.includes(change.fieldId)) {
        errors.push({ fieldId: change.fieldId, reason: 'FIELD_NOT_FOUND' });
        continue;
      }
      if (DERIVED_FIELD_TYPES.has(field.type)) {
        errors.push({ fieldId: change.fieldId, reason: 'FIELD_NOT_WRITABLE' });
        continue;
      }

      const value = createEntryValueValidator.safeParse(change.value);
      if (!value.success || value.data.type !== fieldTypeMap[field.type]) {
        errors.push({ fieldId: change.fieldId, reason: 'INCOMPATIBLE_VALUE' });
        continue;
      }
      const collabValue = valueToCollabJSON(value.data as DatabaseValue, {});
      if (!collabValue) {
        errors.push({ fieldId: change.fieldId, reason: 'FIELD_NOT_WRITABLE' });
        continue;
      }
      changes.push({
        fieldId: change.fieldId,
        fieldType: field.type,
        value: collabValue
      });
    }

    if (errors.length > 0) {
      return err({
        type: 'CREATE_ENTRY_VALIDATION_ERROR',
        fieldErrors: errors
      });
    }

    return ok(changes);
  }

  remove(input: RemoveEntryInput): Result<void, SdkError> {
    return parse(removeEntrySchema, input).andThen(({ entryId }) =>
      assertEntryExists(this.ydb, entryId).andThen(() =>
        tryMutate(() => deleteEntry(this.ydb, entryId))
      )
    );
  }

  reorder(input: ReorderEntriesInput): Result<void, SdkError> {
    return parse(reorderEntriesSchema, input).andThen(({ entryIds, newIndex }) => {
      for (const id of entryIds) {
        const check = assertEntryExists(this.ydb, id);

        if (check.isErr()) {
          return err(check.error);
        }
      }

      return assertIndexInRange(
        newIndex,
        this.ydb.entries.length - entryIds.length
      ).andThen(() => tryMutate(() => reorderEntries(this.ydb, { entryIds, newIndex })));
    });
  }
}
